import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  ShieldCheck,
  Wind,
  Home as HomeIcon,
  Hammer,
  ClipboardCheck,
  Clock,
  Award,
  Map as MapIcon,
  Star,
  Quote,
} from "lucide-react";
import { useT } from "../i18n/LanguageContext";

const heroImage =
  "https://static.prod-images.emergentagent.com/jobs/2b9abe43-28cb-4bd1-9e3c-60fa851731cb/images/65cf9bf8bdc13a4bcb7515aa5fe0ec1d1890a11e8326b59c6e71441725724b20.png";

export default function Home() {
  const { t } = useT();
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section data-testid="home-hero" className="relative min-h-[92vh] flex items-end overflow-hidden">
        <img src={heroImage} alt="Florida home" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#1F3460]/55" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1F3460] via-[#1F3460]/30 to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8 pb-20 md:pb-28 pt-32 w-full">
          <div className="max-w-3xl fade-up">
            <div className="overline text-[#6FA8DC]">{t("home.hero.overline")}</div>
            <h1 className="font-display text-5xl md:text-7xl font-black text-white mt-5 leading-[0.95] drop-shadow-lg">
              {t("home.hero.title1")} <br />
              {t("home.hero.title2")} <br />
              <span className="text-[#6FA8DC]">{t("home.hero.title3")}</span>
            </h1>
            <p className="mt-6 text-lg md:text-xl text-slate-200 max-w-2xl">{t("home.hero.subtitle")}</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/appointment" data-testid="hero-cta-appointment" className="btn-accent">
                {t("home.hero.cta1")} <ArrowUpRight className="w-4 h-4" />
              </Link>
              <Link to="/appointment" data-testid="hero-cta-services" className="btn-outline-light">
                {t("home.hero.cta2")}
              </Link>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 z-10 border-t border-white/15 bg-[#1F3460]/70 backdrop-blur-md">
          <div className="max-w-7xl mx-auto px-5 md:px-8 py-5 grid grid-cols-2 md:grid-cols-4 gap-6 text-white">
            {[
              { icon: ShieldCheck, key: "home.hero.badge1" },
              { icon: Award, key: "home.hero.badge2" },
              { icon: Clock, key: "home.hero.badge3" },
              { icon: MapIcon, key: "home.hero.badge4" },
            ].map((b, i) => (
              <div key={i} className="flex items-center gap-3">
                <b.icon className="w-5 h-5 text-[#6FA8DC]" />
                <span className="text-sm tracking-wider uppercase font-bold">{t(b.key)}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section data-testid="home-services" className="py-24 md:py-32 px-5 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
            <div className="max-w-2xl">
              <div className="overline">{t("home.services.overline")}</div>
              <h2 className="font-display text-4xl md:text-6xl font-black text-[#1F3460] mt-3 leading-[1.05]">
                {t("home.services.title")}
              </h2>
            </div>
            <Link to="/appointment" data-testid="services-link-all" className="btn-outline">
              {t("home.services.cta")} <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { key: "four-point", num: "01", tag: "home.services.4pt.tag", title: "home.services.4pt.title", copy: "home.services.4pt.copy", icon: ClipboardCheck },
              { key: "wind", num: "02", tag: "home.services.wind.tag", title: "home.services.wind.title", copy: "home.services.wind.copy", icon: Wind },
              { key: "full", num: "03", tag: "home.services.full.tag", title: "home.services.full.title", copy: "home.services.full.copy", icon: HomeIcon },
              { key: "roof", num: "04", tag: "home.services.roof.tag", title: "home.services.roof.title", copy: "home.services.roof.copy", icon: Hammer },
            ].map((s) => (
              <div key={s.key} data-testid={`service-card-${s.key}`} className="bg-white border border-[#E2E8F0] p-8 flex flex-col justify-between min-h-[340px] group hover:bg-slate-50 hover:border-[#1F3460] hover:-translate-y-1 hover:shadow-2xl transition-all duration-300">
                <div className="flex items-start justify-between">
                  <s.icon className="w-10 h-10 text-[#1F3460] group-hover:text-[#6FA8DC] transition-colors" />
                  <span className="font-display font-black text-3xl text-slate-200 group-hover:text-[#6FA8DC]/40 transition-colors">{s.num}</span>
                </div>
                <div>
                  <div className="overline">{t(s.tag)}</div>
                  <h3 className="font-display text-2xl font-black text-[#1F3460] mt-2 leading-tight">{t(s.title)}</h3>
                  <p className="mt-3 text-[#5E6B85] text-sm leading-relaxed">{t(s.copy)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY */}
      <section data-testid="home-why" className="py-24 md:py-32 px-5 md:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="overline">{t("home.why.overline")}</div>
            <h2 className="font-display text-4xl md:text-6xl font-black text-[#1F3460] mt-4 leading-[1.05]">
              {t("home.why.title1")} <span className="text-[#6FA8DC]">{t("home.why.title2")}</span>
            </h2>
            <p className="mt-6 text-[#5E6B85] max-w-md">{t("home.why.body")}</p>
          </div>
          <div className="md:col-span-7 space-y-6">
            {[
              { n: "01", title: "home.why.item1.title", copy: "home.why.item1.copy" },
              { n: "02", title: "home.why.item2.title", copy: "home.why.item2.copy" },
              { n: "03", title: "home.why.item3.title", copy: "home.why.item3.copy" },
              { n: "04", title: "home.why.item4.title", copy: "home.why.item4.copy" },
            ].map((f, i) => (
              <div key={i} data-testid={`why-item-${i}`} className="relative pl-20 pr-6 py-6 border-l-4 border-[#1F3460] bg-white hover:border-[#6FA8DC] transition-colors group">
                <span className="absolute left-4 top-1 font-display font-black text-5xl text-slate-200 group-hover:text-[#6FA8DC]/30 transition-colors">{f.n}</span>
                <h3 className="font-display text-xl md:text-2xl font-black text-[#1F3460]">{t(f.title)}</h3>
                <p className="mt-2 text-[#5E6B85]">{t(f.copy)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section data-testid="home-testimonials" className="py-24 md:py-32 px-5 md:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
            <div className="max-w-2xl">
              <div className="overline">{t("home.test.overline")}</div>
              <h2 className="font-display text-4xl md:text-6xl font-black text-[#1F3460] mt-3 leading-[1.05]">{t("home.test.title")}</h2>
            </div>
            <div className="flex items-center gap-2 text-[#1F3460]">
              <div className="flex">{[...Array(5)].map((_, i) => (<Star key={i} className="w-5 h-5 fill-[#6FA8DC] text-[#6FA8DC]" />))}</div>
              <span className="font-bold text-sm tracking-wider uppercase">{t("home.test.avg")}</span>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#E2E8F0] border border-[#E2E8F0]">
            {[
              { q: "home.test.q1", name: "home.test.r1.name", role: "home.test.r1.role" },
              { q: "home.test.q2", name: "home.test.r2.name", role: "home.test.r2.role" },
              { q: "home.test.q3", name: "home.test.r3.name", role: "home.test.r3.role" },
            ].map((tn, i) => (
              <div key={i} data-testid={`testimonial-${i}`} className="bg-white p-8 md:p-10 flex flex-col justify-between hover:bg-slate-50 transition-colors">
                <div>
                  <Quote className="w-8 h-8 text-[#6FA8DC]" />
                  <p className="mt-5 text-[#1F3460] text-lg leading-relaxed">"{t(tn.q)}"</p>
                </div>
                <div className="mt-8 pt-6 border-t border-[#E2E8F0]">
                  <div className="font-display font-black text-[#1F3460]">{t(tn.name)}</div>
                  <div className="text-xs uppercase tracking-widest text-[#5E6B85] mt-1">{t(tn.role)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="home-cta" className="bg-[#1F3460] text-white py-24 md:py-32 px-5 md:px-8">
        <div className="max-w-5xl mx-auto text-center">
          <div className="overline text-[#6FA8DC]">{t("home.cta.overline")}</div>
          <h2 className="font-display text-4xl md:text-6xl font-black mt-4 leading-[1.05]">
            {t("home.cta.title1")} <em className="not-italic text-[#6FA8DC]">{t("home.cta.title2")}</em>.
          </h2>
          <p className="mt-6 text-slate-300 max-w-2xl mx-auto">{t("home.cta.body")}</p>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <Link to="/appointment" data-testid="cta-book-now" className="btn-accent">{t("home.cta.btn1")} <ArrowUpRight className="w-4 h-4" /></Link>
            <Link to="/contact" data-testid="cta-contact" className="btn-outline-light">{t("home.cta.btn2")}</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
